import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiEdit2, FiEye, FiPlus, FiSearch, FiTrash2 } from 'react-icons/fi';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import AdsModal from '../components/AdsModal';
import ViewAdsModal from '../components/ViewAdsModal';
import AdsClicksSparkline from '../components/AdsClicksSparkline';

const AdsPage = () => {
    const [ads, setAds] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalAds, setTotalAds] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [selectedAd, setSelectedAd] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'ngrok-skip-browser-warning': 'true'
    });

    const fetchCategories = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/ads/categories`,
                { headers: authHeaders() }
            );
            if (response.data.success) {
                setCategories(response.data.data || []);
            }
        } catch {
            setCategories([]);
        }
    };

    const fetchAds = async (page = 1, search = '', category = '') => {
        try {
            setLoading(true);
            let apiUrl = `${process.env.REACT_APP_API_URL}/api/ads?page=${page}&limit=20&sort=-adsID`;
            if (search) apiUrl += `&search=${encodeURIComponent(search)}`;
            if (category) apiUrl += `&category=${encodeURIComponent(category)}`;

            const response = await axios.get(apiUrl, { headers: authHeaders() });

            if (response.data.success) {
                setAds(response.data.data);
                setTotalAds(response.data.pagination.total);
                setTotalPages(response.data.pagination.pages);
            }
        } catch (err) {
            setError(`Failed to fetch ads: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        fetchAds(currentPage, searchTerm, categoryFilter);
    }, [currentPage, searchTerm, categoryFilter]);

    const handleSearch = () => {
        setSearchTerm(searchInput);
        setCurrentPage(1);
    };

    const handleClear = () => {
        setSearchInput('');
        setSearchTerm('');
        setCategoryFilter('');
        setCurrentPage(1);
    };

    const handleDelete = async () => {
        if (!selectedAd) return;
        try {
            setDeleteLoading(true);
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/ads/${selectedAd._id}`,
                { headers: authHeaders() }
            );
            setShowDeleteModal(false);
            setSelectedAd(null);
            fetchAds(currentPage, searchTerm, categoryFilter);
            fetchCategories();
        } catch (err) {
            setError(`Failed to delete ad: ${err.response?.data?.error || err.message}`);
        } finally {
            setDeleteLoading(false);
        }
    };

    const formatDate = (value) => {
        if (!value) return null;
        const d = new Date(value);
        if (!Number.isFinite(d.getTime())) return null;
        return d.toLocaleDateString('en-SG');
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">Ads</h1>
                        <p className="text-gray-600">Total: {totalAds} ads</p>
                    </div>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                    >
                        <FiPlus className="mr-2" />
                        Create New Ad
                    </button>
                </div>

                <div className="mb-6 flex flex-col lg:flex-row gap-2">
                    <div className="relative flex-1">
                        <input
                            type="text"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-blue-700 font-bold"
                            placeholder="Search by ads ID, category, URLs, sources..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <FiSearch className="h-5 w-5 text-gray-400" />
                        </div>
                    </div>
                    <select
                        value={categoryFilter}
                        onChange={(e) => {
                            setCategoryFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-md text-blue-700 font-bold min-w-[180px]"
                    >
                        <option value="">All Categories</option>
                        {categories.map((cat) => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                    <button onClick={handleSearch} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center">
                        <FiSearch className="mr-2" />
                        Search
                    </button>
                    <button onClick={handleClear} className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700">
                        Clear
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                {showCreateModal && (
                    <AdsModal
                        mode="create"
                        onSuccess={() => {
                            setShowCreateModal(false);
                            fetchAds(currentPage, searchTerm, categoryFilter);
                            fetchCategories();
                        }}
                        onCancel={() => setShowCreateModal(false)}
                    />
                )}

                {showEditModal && selectedAd && (
                    <AdsModal
                        mode="edit"
                        ad={selectedAd}
                        onSuccess={() => {
                            setShowEditModal(false);
                            setSelectedAd(null);
                            fetchAds(currentPage, searchTerm, categoryFilter);
                            fetchCategories();
                        }}
                        onCancel={() => {
                            setShowEditModal(false);
                            setSelectedAd(null);
                        }}
                    />
                )}

                {showDeleteModal && selectedAd && (
                    <DeleteConfirmationModal
                        title="Delete Ad"
                        message="Are you sure you want to delete this ad?"
                        itemName={`#${selectedAd.adsID} - ${selectedAd.adsCategory || 'Uncategorized'}`}
                        isLoading={deleteLoading}
                        onConfirm={handleDelete}
                        onCancel={() => {
                            setShowDeleteModal(false);
                            setSelectedAd(null);
                        }}
                    />
                )}

                {showViewModal && selectedAd && (
                    <ViewAdsModal
                        ad={selectedAd}
                        onClose={() => {
                            setShowViewModal(false);
                            setSelectedAd(null);
                        }}
                    />
                )}

                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ads ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Media</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sources</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-4 text-center">Loading...</td>
                                </tr>
                            ) : ads.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-4 text-center">No ads found</td>
                                </tr>
                            ) : (
                                ads.map((ad) => (
                                    <tr key={ad._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{ad.adsID}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{ad.adsCategory || 'N/A'}</td>
                                        <td className="px-6 py-4 text-sm">
                                            {(ad.adsImage?.length || 0)} img / {(ad.adsGif?.length || 0)} gif / {(ad.adsVideos?.length || 0)} vid
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{ad.adsSource?.length || 0}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{formatDate(ad.updatedAt)}</td>
                                        <td className="px-6 py-4">
                                            <AdsClicksSparkline adsClicks={ad.adsClicks} />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                            <button onClick={() => { setSelectedAd(ad); setShowViewModal(true); }} className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-full inline-flex items-center" title="View Details">
                                                <FiEye className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => { setSelectedAd(ad); setShowEditModal(true); }} className="p-1.5 text-green-600 hover:text-green-900 hover:bg-green-100 rounded-full inline-flex items-center" title="Edit Ad">
                                                <FiEdit2 className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => { setSelectedAd(ad); setShowDeleteModal(true); }} className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-full inline-flex items-center" title="Delete Ad">
                                                <FiTrash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
        </Layout>
    );
};

export default AdsPage;
